import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { CornerDownRight } from 'lucide-react';
import { useNodeStore } from '@/stores/node-store';
import type { CustomNode } from '@/types/node-types';
import type { CustomEdge } from '@/types/edge-types';
import {
  getConditionalBranchLabel,
  isConditionalEdge,
} from '@/pages/workflow/helper/functional-control-flow';
import {
  getSkipExplanation,
  skippedSentence,
  type SkipExplanation,
} from '@/pages/workflow/helper/decision-trace';

export interface ResolvedSkip {
  explanation: SkipExplanation;
  switchName: string | null;
  sentence: string;
}

const nodeName = (node: CustomNode | undefined): string | null => {
  if (!node) return null;
  const struct = node.data?.struct as
    | { user_config_name?: string; display_name?: string }
    | undefined;
  const name =
    (node.data as { display_name?: string; name?: string } | undefined)
      ?.display_name ??
    struct?.user_config_name ??
    (node.data as { name?: string } | undefined)?.name ??
    struct?.display_name;
  return typeof name === 'string' && name ? name : null;
};

/**
 * Fill in what an older skip marker lacks from the canvas: the Switch's name
 * from the node list, and the branch labels from the conditional edges that
 * lead from that Switch to this node.
 */
export const resolveSkip = (
  result: unknown,
  nodeId: string,
  nodes: CustomNode[],
  edges: CustomEdge[],
): ResolvedSkip | null => {
  const explanation = getSkipExplanation(result);
  if (!explanation) return null;
  const switchNode = explanation.switchId
    ? nodes.find((node) => node.id === explanation.switchId)
    : undefined;
  const switchName = explanation.switchName ?? nodeName(switchNode);
  let labels = explanation.notTakenLabels;
  if (labels.length === 0 && explanation.switchId) {
    labels = edges
      .filter(
        (edge) =>
          edge.source === explanation.switchId &&
          edge.target === nodeId &&
          isConditionalEdge(edge),
      )
      .map(getConditionalBranchLabel)
      .filter((label): label is string => Boolean(label));
  }
  const filled = { ...explanation, notTakenLabels: labels };
  return {
    explanation: filled,
    switchName,
    sentence: skippedSentence(filled, switchName),
  };
};

export function useResolvedSkip(
  nodeId: string,
  result: unknown,
): ResolvedSkip | null {
  const { nodes, edges } = useNodeStore(
    useShallow((state) => ({ nodes: state.nodes, edges: state.edges })),
  );
  return useMemo(
    () => resolveSkip(result, nodeId, nodes as CustomNode[], edges),
    [result, nodeId, nodes, edges],
  );
}

/**
 * The chip a node wears when a Switch upstream took another branch:
 * "skipped: branch «fizz» not taken", with the Switch's choice on hover.
 */
export function SkippedBranchChip({
  nodeId,
  result,
}: {
  nodeId: string;
  result: unknown;
}) {
  const resolved = useResolvedSkip(nodeId, result);
  if (!resolved) return null;
  const { explanation, switchName, sentence } = resolved;
  return (
    <div
      className='mx-3 mb-1 flex items-center gap-1.5 rounded-md border border-dashed border-violet-300 bg-violet-50/70 px-2 py-1 text-[11px] text-violet-900 dark:border-violet-700 dark:bg-violet-950/30 dark:text-violet-200'
      data-testid='skipped-branch-chip'
      data-switch-id={explanation.switchId ?? undefined}
      data-selected-label={explanation.selectedLabel ?? undefined}
      title={sentence}
    >
      <CornerDownRight className='size-3 shrink-0' />
      <span className='truncate'>
        <span className='font-medium'>skipped</span>
        {': '}
        {explanation.notTakenLabels.length > 0
          ? `branch «${explanation.notTakenLabels.join('», «')}» not taken`
          : 'branch not taken'}
        {switchName && explanation.selectedLabel
          ? ` · ${switchName} took «${explanation.selectedLabel}»`
          : ''}
      </span>
    </div>
  );
}
